import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useIsPro from "../../hooks/useIsPro";
import api from "../../api/api";
import "./TrackAssessment.css";
import ReviewModal from "../../components/Track/ReviewModal";
import UpgradeTriggerModal from "../../components/Track/UpgradeTriggerModal";

function TrackAssessment() {
  const { slug, phaseId } = useParams();
  const navigate = useNavigate();
  const isPro = useIsPro();


  const [track, setTrack] = useState(null);
  const [phase, setPhase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [quiz, setQuiz] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [quizResults, setQuizResults] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isPhase1Finished, setIsPhase1Finished] = useState(false);

  useEffect(() => {
    const fetchTrackData = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/tracks/${slug}`);
        const t = res.data.track;
        setTrack(t);

        const currentPhase = t.phases.find(p => p._id === phaseId);
        if (!currentPhase) {
          setError("Phase not found");
          setLoading(false);
          return;
        }

        setPhase(currentPhase);

        // Allow free users only on Phase 1 (order === 1)
        // All other phases require Pro
        if (!isPro && currentPhase.order !== 1) {
          navigate("/upgrade");
          return;
        }

        setLoading(false);
      } catch (err) {
        console.error(err);
        setError("Failed to load assessment data.");
        setLoading(false);
      }
    };

    fetchTrackData();
  }, [slug, phaseId, isPro, navigate]);

  const handleStartAssessment = async () => {
    if (!phase || !phase.assessmentVideoIds?.length) {
      setError("No assessment videos found for this phase.");
      return;
    }

    try {
      setQuizLoading(true);
      // We use the first assessment video ID and request 10 questions for the phase assessment
      const videoId = phase.assessmentVideoIds[0];
      const res = await api.get(`/quiz/${videoId}?difficulty=hard&count=10&title=${encodeURIComponent(phase.title)}`);
      setQuiz(res.data);
      setQuizStarted(true);
      setQuizLoading(false);
    } catch (err) {
      console.error(err);
      setError("Failed to generate assessment. Please try again.");
      setQuizLoading(false);
    }
  };

  const handleSelectOption = (questionIndex, optionIndex) => {
    setUserAnswers(prev => ({ ...prev, [questionIndex]: optionIndex }));
  };

  const handleSubmitAssessment = async () => {
    try {
      setSubmittingQuiz(true);
      const answersArray = Object.entries(userAnswers).map(([qi, oi]) => ({
        questionIndex: parseInt(qi),
        selectedOption: oi
      }));

      const res = await api.post("/quiz/submit", {
        quizId: quiz.quizId,
        videoId: quiz.videoId,
        answers: answersArray,
        trackId: track._id,
        phaseId: phase._id
      });

      setQuizResults(res.data);
      setSubmittingQuiz(false);

      if (res.data.passed) {
        setShowReviewModal(true);
        if (res.data.isPhase1Completion) {
          setIsPhase1Finished(true);
        }
      }
    } catch (err) {
      console.error(err);
      setSubmittingQuiz(false);
    }
  };

  if (loading) return <div className="assessment-page">Loading assessment...</div>;
  if (error) return <div className="assessment-page"><div className="assessment-card"><p>{error}</p><button onClick={() => navigate(-1)}>Back</button></div></div>;

  const progress = quizStarted && quiz ? ((currentQuestionIndex + 1) / quiz.questions.length) * 100 : 0;

  return (
    <div className="assessment-page">
      <div className="assessment-card">
        {quizStarted && !quizResults && <div className="assessment-progress-bar" style={{ width: `${progress}%` }} />}

        <div className="assessment-header">
          <p>{track?.title} • Phase {phase?.order}</p>
          <h1>{phase?.title} Assessment</h1>
        </div>

        {!quizStarted ? (
          <div className="assessment-intro">
            <span className="assessment-intro-icon">📝</span>
            <h2>Milestone Assessment</h2>
            <p>
              This assessment tests your knowledge of the key concepts covered in this phase.
              You need at least <strong>70%</strong> to pass and unlock the next phase.
            </p>
            <p style={{ fontSize: '14px', fontStyle: 'italic' }}>10 Questions • Multiple Choice • Hard Difficulty</p>
            <button className="start-assessment-btn" onClick={handleStartAssessment} disabled={quizLoading}>
              {quizLoading ? "Preparing Assessment..." : "Begin Assessment"}
            </button>
          </div>
        ) : quizResults ? (
          <div className="assessment-results">
            <div className="results-hero">
              <div className={`large-score-circle ${!quizResults.passed ? 'failed' : ''}`}>
                <span className="score">{quizResults.score}%</span>
                <span className="label">SCORE</span>
              </div>
              <h2>{quizResults.passed ? "Congratulations!" : "Not Quite There"}</h2>
              <p>
                {quizResults.passed
                  ? "You've successfully mastered this phase. You can now proceed to the next step of your journey."
                  : `You got ${quizResults.correctCount} out of ${quizResults.totalQuestions} correct. You need 70% to pass this phase.`}
              </p>

              <div className="results-actions">
                {quizResults.passed ? (
                  <button className="action-btn primary" onClick={() => navigate(`/tracks/${slug}`)}>
                    Continue Track
                  </button>
                ) : (
                  <button className="action-btn primary" onClick={() => {
                    setQuizResults(null);
                    setQuizStarted(false);
                    setCurrentQuestionIndex(0);
                    setUserAnswers({});
                  }}>
                    Try Again
                  </button>
                )}
                {!quizResults.passed && (
                  <button className="action-btn secondary" onClick={() => navigate(`/tracks/${slug}`)}>
                    Back to Track Detail
                  </button>
                )}
              </div>
            </div>

            {!quizResults.passed && (
              <div className="assessment-rewatch-suggestions">
                <h3>💡 Recommended Review</h3>
                <p style={{ marginBottom: '20px', color: 'var(--color-text-muted)' }}>
                  We recommend reviewing these phase resources before your next attempt:
                </p>
                <div className="suggestion-list">
                  {phase?.resources?.slice(0, 4).map((res, i) => (
                    <div
                      key={i}
                      className="suggestion-item"
                      onClick={() => {
                        if (res.courseId) {
                          navigate(`/course/${res.courseId._id}`, { state: { from: 'track', trackSlug: slug, phaseId: phase._id } });
                        } else if (res.url) {
                          window.open(res.url, '_blank');
                        }
                      }}
                    >
                      <div className="suggestion-icon">▶</div>
                      <div className="suggestion-info">
                        <h4>{res.label}</h4>
                        <span>{res.type.toUpperCase()} • {res.estimatedMinutes} mins</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : quiz ? (
          <div className="question-section">
            <div className="question-counter">Question {currentQuestionIndex + 1} of {quiz.questions.length}</div>
            <div className="assessment-question-text">{quiz.questions[currentQuestionIndex].q}</div>

            <div className="assessment-options">
              {quiz.questions[currentQuestionIndex].options.map((opt, i) => (
                <button
                  key={i}
                  className={`assessment-option ${userAnswers[currentQuestionIndex] === i ? 'selected' : ''}`}
                  onClick={() => handleSelectOption(currentQuestionIndex, i)}
                >
                  <span className="option-letter">{String.fromCharCode(65 + i)}</span>
                  {opt}
                </button>
              ))}
            </div>

            <div className="assessment-footer">
              <button
                className="nav-btn prev"
                disabled={currentQuestionIndex === 0}
                onClick={() => setCurrentQuestionIndex(prev => prev - 1)}
              >
                Previous
              </button>

              {currentQuestionIndex < quiz.questions.length - 1 ? (
                <button
                  className="nav-btn next"
                  disabled={userAnswers[currentQuestionIndex] === undefined}
                  onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="nav-btn submit"
                  disabled={Object.keys(userAnswers).length < quiz.questions.length || submittingQuiz}
                  onClick={handleSubmitAssessment}
                >
                  {submittingQuiz ? "Submitting..." : "Complete Assessment"}
                </button>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {showReviewModal && (
        <ReviewModal
          trackId={track?._id}
          phaseId={phase?._id}
          phaseOrder={phase?.order}
          onComplete={() => {
            setShowReviewModal(false);
            if (isPhase1Finished) {
              setShowUpgradeModal(true);
            }
          }}
        />
      )}

      {showUpgradeModal && (
        <UpgradeTriggerModal
          onClose={() => setShowUpgradeModal(false)}
        />
      )}
    </div>
  );
}

export default TrackAssessment;
